import { PanelBoundary } from './PanelBoundary.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Container, Header, SkipLink } from '@meu-mapa-nev/design-system';

import { AlertComFoco } from './AlertComFoco.js';
import { adminApi, type TrustedDevice } from './acesso/api.js';
import { AdesaoDispositivo } from './acesso/AdesaoDispositivo.js';
import { TelaDeDispositivos } from './acesso/TelaDeDispositivos.js';
import { TelaDeLoginAdmin, type EtapaLogin } from './acesso/TelaDeLoginAdmin.js';
import { turnstileAntibot, type Antibot } from './antibot.js';
import { documentosApi, type AdminDocument } from './documentos/api.js';
import {
  turmasApi,
  type CohortAction,
  type CohortDetail,
  type CohortParticipant,
  type CohortSummary,
  type FinalParticipationStatus,
  type InitialCorrectionStatus,
  type ExceptionalComparisonAccepted,
} from './turmas/api.js';

const TelaDeLiberacao = React.lazy(() =>
  import('./liberacao/TelaDeLiberacao.js').then((module) => ({
    default: module.TelaDeLiberacao,
  })),
);
const TelaDeTurma = React.lazy(() =>
  import('./turmas/TelaDeTurma.js').then((module) => ({ default: module.TelaDeTurma })),
);
const TelaDeTurmas = React.lazy(() =>
  import('./turmas/TelaDeTurmas.js').then((module) => ({ default: module.TelaDeTurmas })),
);

const TelaEditorial = React.lazy(() =>
  import('./editorial/TelaEditorial.js').then((module) => ({ default: module.TelaEditorial })),
);
const TelaDePrivacidade = React.lazy(() =>
  import('./privacidade/TelaDePrivacidade.js').then((module) => ({
    default: module.TelaDePrivacidade,
  })),
);
const TelaDeBackups = React.lazy(() =>
  import('./backups/TelaDeBackups.js').then((module) => ({ default: module.TelaDeBackups })),
);
const TelaDeConteudo = React.lazy(() =>
  import('./conteudo/TelaDeConteudo.js').then((module) => ({ default: module.TelaDeConteudo })),
);
const TelaDeOperacao = React.lazy(() =>
  import('./operacao/TelaDeOperacao.js').then((module) => ({ default: module.TelaDeOperacao })),
);
const ParticipantesPage = React.lazy(() =>
  import('./participantes/ParticipantesPage.js').then((module) => ({
    default: module.ParticipantesPage,
  })),
);
const TelaDeMetodologias = React.lazy(() =>
  import('./metodologia/TelaDeMetodologias.js').then((module) => ({
    default: module.TelaDeMetodologias,
  })),
);

/**
 * Aplicação da administração.
 *
 * Mesma correção estrutural da aplicação da participante: as telas do Bloco 5
 * da Fase 2 existiam e estavam testadas, mas este arquivo mostrava a vitrine da
 * Fase 1 e ninguém as alcançava.
 *
 * Desktop-first com equivalência no celular, como as fontes exigem: uma coluna
 * única que ganha densidade em telas largas, sem nenhuma ação sensível que
 * dependa de passar o mouse por cima ou que suma em tela pequena.
 */

interface Dependencias {
  readonly api: typeof adminApi;
  readonly turmasApi: typeof turmasApi;
  readonly documentosApi: typeof documentosApi;
  readonly antibot: Antibot;
}

export interface AppProps {
  readonly deps?: Partial<Dependencias>;
}

type Situacao =
  | { readonly tipo: 'verificando-sessao' }
  | { readonly tipo: 'contexto-indisponivel' }
  | { readonly tipo: 'login'; readonly etapa: EtapaLogin }
  | { readonly tipo: 'adesao-dispositivo' }
  | { readonly tipo: 'metodologias' }
  | { readonly tipo: 'participantes'; readonly participationId?: string }
  | { readonly tipo: 'editorial' }
  | { readonly tipo: 'privacidade' }
  | { readonly tipo: 'backups' }
  | { readonly tipo: 'conteudo' }
  | { readonly tipo: 'operacao'; readonly personId?: string; readonly participationId?: string }
  | { readonly tipo: 'dispositivos'; readonly devices: readonly TrustedDevice[] }
  | { readonly tipo: 'turmas'; readonly cohorts: readonly CohortSummary[] }
  | {
      readonly tipo: 'turma';
      readonly cohort: CohortDetail;
      readonly participants: readonly CohortParticipant[];
    };

const FINALIDADE_REVOGACAO = 'admin.revoke-devices';
const FINALIDADE_TURMA_ACAO = 'admin.cohort-action';
const FINALIDADE_DOWNLOAD_DOCUMENTO = 'admin.document-download';
const FINALIDADE_REABERTURA_FINAL = 'admin.final-reopen';
const FINALIDADE_REABERTURA_INICIAL = 'admin.initial-reopen';
const FINALIDADE_COMPARATIVO_EXCEPCIONAL = 'admin.exceptional-comparison';

/**
 * Recipiente do desafio antibot.
 *
 * A montagem acontece num efeito, e não durante a renderização, porque envolve
 * carregar um script externo. Se a chave pública não estiver configurada, o
 * módulo não desenha nada e o recipiente fica vazio — a tela só descobre o
 * problema quando alguém tenta enviar, e aí a mensagem é específica.
 */
const DesafioAntibot: React.FC<{ readonly antibot: Antibot }> = ({ antibot }) => {
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const alvo = container.current;
    if (alvo === null) {
      return;
    }
    void antibot.montar(alvo);
    return () => {
      antibot.desmontar();
    };
  }, [antibot]);

  return <div ref={container} data-testid="desafio-antibot" />;
};

export const App: React.FC<AppProps> = ({ deps }) => {
  const api = deps?.api ?? adminApi;
  const turmas = deps?.turmasApi ?? turmasApi;
  const documentos = deps?.documentosApi ?? documentosApi;
  const antibot = deps?.antibot ?? turnstileAntibot;

  const [situacao, setSituacao] = useState<Situacao>({ tipo: 'verificando-sessao' });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | undefined>(undefined);
  const [desafioId, setDesafioId] = useState<string | null>(null);
  // Guarda a finalidade junto do token: as duas ações sensíveis (revogar
  // dispositivo, agir sobre turma) usam finalidades diferentes, e uma prova
  // válida para uma não pode parecer disponível para a outra na tela — o
  // servidor recusaria, mas a interface não deve nem sugerir que serviria.
  const [provaReautenticacao, setProvaReautenticacao] = useState<{
    token: string;
    purpose: string;
    participationId?: string;
  } | null>(null);
  // Cada desafio de segundo passo vale para uma finalidade só. Guardar qual
  // está em curso evita verificar um código de login como se fosse a prova de
  // revogação — a finalidade é conferida no servidor, e trocá-la aqui só
  // produziria um erro confuso.
  const [finalidadeEmCurso, setFinalidadeEmCurso] = useState<string>('admin.login');
  // A confirmação de identidade para uma ação de turma passa pela MESMA tela
  // de código do login — guardar qual turma estava aberta é o que permite
  // voltar a ela, e não à lista, depois de confirmar.
  const [turmaEmFocoId, setTurmaEmFocoId] = useState<string | null>(null);
  const [participacaoDocumentosEmFoco, setParticipacaoDocumentosEmFoco] = useState<string | null>(
    null,
  );
  const [painelDocumentos, setPainelDocumentos] = useState<{
    participationId: string;
    items: readonly AdminDocument[];
  } | null>(null);
  const [participacaoFinalEmFoco, setParticipacaoFinalEmFoco] = useState<string | null>(null);
  const [painelFinal, setPainelFinal] = useState<FinalParticipationStatus | null>(null);
  const [finalReopenReason, setFinalReopenReason] = useState('');
  const [finalIdentityConfirmed, setFinalIdentityConfirmed] = useState(false);
  const [participacaoInicialEmFoco, setParticipacaoInicialEmFoco] = useState<string | null>(null);
  const [painelCorrecaoInicial, setPainelCorrecaoInicial] =
    useState<InitialCorrectionStatus | null>(null);
  const [comparativoExcepcional, setComparativoExcepcional] =
    useState<ExceptionalComparisonAccepted | null>(null);
  const [documentoEmDownload, setDocumentoEmDownload] = useState<string | null>(null);

  const carregarDispositivos = useCallback(async (): Promise<void> => {
    const resultado = await api.listDevices();
    if (resultado.ok) {
      setSituacao({ tipo: 'dispositivos', devices: resultado.value.items });
      return;
    }
    setErro(resultado.problem.title);
  }, [api]);

  const carregarTurmas = useCallback(async (): Promise<void> => {
    const resultado = await turmas.list();
    if (resultado.ok) {
      setSituacao({ tipo: 'turmas', cohorts: resultado.value.cohorts });
      return;
    }
    setErro(resultado.problem.title);
  }, [turmas]);

  const abrirTurma = useCallback(
    async (cohortId: string): Promise<void> => {
      setTurmaEmFocoId(cohortId);
      const [detalhe, participantes] = await Promise.all([
        turmas.get(cohortId),
        turmas.listParticipants(cohortId),
      ]);
      if (!detalhe.ok) {
        setErro(detalhe.problem.title);
        return;
      }
      setSituacao({
        tipo: 'turma',
        cohort: detalhe.value,
        participants: participantes.ok ? participantes.value.participants : [],
      });
    },
    [turmas],
  );

  const carregarDocumentos = useCallback(
    async (participationId: string): Promise<void> => {
      setParticipacaoDocumentosEmFoco(participationId);
      const result = await documentos.list(participationId);
      if (!result.ok) {
        setErro(result.problem.title);
        return;
      }
      setPainelDocumentos({ participationId, items: result.value.items });
    },
    [documentos],
  );

  const carregarStatusFinal = useCallback(
    async (participationId: string): Promise<void> => {
      setParticipacaoFinalEmFoco(participationId);
      const result = await turmas.finalStatus(participationId);
      if (!result.ok) {
        setErro(result.problem.title);
        return;
      }
      setPainelFinal(result.value);
    },
    [turmas],
  );

  const carregarStatusCorrecaoInicial = useCallback(
    async (participationId: string): Promise<void> => {
      setParticipacaoInicialEmFoco(participationId);
      const result = await turmas.initialCorrectionStatus(participationId);
      if (!result.ok) {
        setErro(result.problem.title);
        return;
      }
      setPainelCorrecaoInicial(result.value);
    },
    [turmas],
  );

  const verificarSessao = useCallback(
    async (ativo: () => boolean = () => true): Promise<void> => {
      try {
        const resultado = await api.listDevices();
        if (!ativo()) return;
        if (resultado.ok) {
          setSituacao({ tipo: 'dispositivos', devices: resultado.value.items });
        } else {
          setSituacao(
            resultado.problem.status === 401
              ? { tipo: 'login', etapa: 'senha' }
              : { tipo: 'contexto-indisponivel' },
          );
        }
      } catch {
        if (ativo()) setSituacao({ tipo: 'contexto-indisponivel' });
      }
    },
    [api],
  );

  useEffect(() => {
    let ativo = true;
    void verificarSessao(() => ativo);
    return () => {
      ativo = false;
    };
  }, [verificarSessao]);

  const entrarComSenha = (email: string, password: string): void => {
    setEnviando(true);
    setErro(undefined);

    void (async () => {
      const tokenAntibot = await antibot.obterToken();
      if (tokenAntibot === null) {
        setErro(antibot.motivoIndisponivel());
        setEnviando(false);
        return;
      }

      const resultado = await api
        .login({ email, password, turnstileToken: tokenAntibot })
        .finally(() => antibot.renovar());
      setEnviando(false);

      if (!resultado.ok) {
        setErro(resultado.problem.title);
        return;
      }

      // O contrato administrativo devolve o identificador do desafio mesmo
      // quando ele é inerte, para que a resposta seja uniforme e não revele se
      // a conta existe. É por isso que este fluxo consegue chegar ao segundo
      // passo, e o da participante não.
      setDesafioId(resultado.value.challenge_id);
      setFinalidadeEmCurso('admin.login');
      setSituacao({ tipo: 'login', etapa: 'segundo-passo' });
    })();
  };

  const conferirSegundoPasso = (code: string): void => {
    if (desafioId === null) {
      return;
    }
    setEnviando(true);
    setErro(undefined);

    void (async () => {
      const resultado = await api.verifyStepUp({
        challengeId: desafioId,
        code,
        purpose: finalidadeEmCurso,
      });
      setEnviando(false);

      if (!resultado.ok) {
        setErro(resultado.problem.title);
        return;
      }

      if (finalidadeEmCurso === FINALIDADE_REVOGACAO) {
        // A prova de reautenticação é de uso único e vale 5 minutos
        // (ADR-0009). Ela volta aqui e habilita a revogação.
        if (resultado.value.reauthentication_token !== null) {
          setProvaReautenticacao({
            token: resultado.value.reauthentication_token,
            purpose: FINALIDADE_REVOGACAO,
          });
        }
        await carregarDispositivos();
        return;
      }

      if (finalidadeEmCurso === FINALIDADE_TURMA_ACAO) {
        if (resultado.value.reauthentication_token !== null) {
          setProvaReautenticacao({
            token: resultado.value.reauthentication_token,
            purpose: FINALIDADE_TURMA_ACAO,
          });
        }
        if (turmaEmFocoId !== null) {
          await abrirTurma(turmaEmFocoId);
        }
        return;
      }

      if (finalidadeEmCurso === FINALIDADE_DOWNLOAD_DOCUMENTO) {
        if (resultado.value.reauthentication_token !== null) {
          setProvaReautenticacao({
            token: resultado.value.reauthentication_token,
            purpose: FINALIDADE_DOWNLOAD_DOCUMENTO,
          });
        }
        if (turmaEmFocoId !== null) await abrirTurma(turmaEmFocoId);
        if (participacaoDocumentosEmFoco !== null) {
          await carregarDocumentos(participacaoDocumentosEmFoco);
        }
        return;
      }

      if (finalidadeEmCurso === FINALIDADE_REABERTURA_FINAL) {
        if (resultado.value.reauthentication_token !== null) {
          setProvaReautenticacao({
            token: resultado.value.reauthentication_token,
            purpose: FINALIDADE_REABERTURA_FINAL,
            ...(participacaoFinalEmFoco === null
              ? {}
              : { participationId: participacaoFinalEmFoco }),
          });
        }
        if (turmaEmFocoId !== null) await abrirTurma(turmaEmFocoId);
        if (participacaoFinalEmFoco !== null) await carregarStatusFinal(participacaoFinalEmFoco);
        return;
      }

      if (
        finalidadeEmCurso === FINALIDADE_REABERTURA_INICIAL ||
        finalidadeEmCurso === FINALIDADE_COMPARATIVO_EXCEPCIONAL
      ) {
        if (resultado.value.reauthentication_token !== null) {
          setProvaReautenticacao({
            token: resultado.value.reauthentication_token,
            purpose: finalidadeEmCurso,
            ...(participacaoInicialEmFoco === null
              ? {}
              : { participationId: participacaoInicialEmFoco }),
          });
        }
        if (turmaEmFocoId !== null) await abrirTurma(turmaEmFocoId);
        if (participacaoInicialEmFoco !== null)
          await carregarStatusCorrecaoInicial(participacaoInicialEmFoco);
        return;
      }

      setSituacao({ tipo: 'adesao-dispositivo' });
    })();
  };

  const pedirReautenticacao = (): void => {
    setEnviando(true);
    setErro(undefined);

    void (async () => {
      const resultado = await api.requestStepUp({ purpose: FINALIDADE_REVOGACAO });
      setEnviando(false);

      if (!resultado.ok) {
        setErro(resultado.problem.title);
        return;
      }

      // Antes da v2.0.3 do contrato este identificador não voltava, e a
      // revogação não tinha como ser concluída pelo navegador.
      setDesafioId(resultado.value.challenge_id);
      setFinalidadeEmCurso(FINALIDADE_REVOGACAO);
      setErro(undefined);
      setSituacao({ tipo: 'login', etapa: 'segundo-passo' });
    })();
  };

  const pedirReautenticacaoTurma = (): void => {
    setEnviando(true);
    setErro(undefined);

    void (async () => {
      const resultado = await api.requestStepUp({ purpose: FINALIDADE_TURMA_ACAO });
      setEnviando(false);

      if (!resultado.ok) {
        setErro(resultado.problem.title);
        return;
      }

      setDesafioId(resultado.value.challenge_id);
      setFinalidadeEmCurso(FINALIDADE_TURMA_ACAO);
      setErro(undefined);
      setSituacao({ tipo: 'login', etapa: 'segundo-passo' });
    })();
  };

  const pedirReautenticacaoDocumento = (participationId: string): void => {
    setEnviando(true);
    setErro(undefined);
    setParticipacaoDocumentosEmFoco(participationId);
    void (async () => {
      const resultado = await api.requestStepUp({ purpose: FINALIDADE_DOWNLOAD_DOCUMENTO });
      setEnviando(false);
      if (!resultado.ok) {
        setErro(resultado.problem.title);
        return;
      }
      setDesafioId(resultado.value.challenge_id);
      setFinalidadeEmCurso(FINALIDADE_DOWNLOAD_DOCUMENTO);
      setSituacao({ tipo: 'login', etapa: 'segundo-passo' });
    })();
  };

  const pedirReautenticacaoFinal = (participationId: string): void => {
    setEnviando(true);
    setErro(undefined);
    setParticipacaoFinalEmFoco(participationId);
    void (async () => {
      const result = await api.requestStepUp({ purpose: FINALIDADE_REABERTURA_FINAL });
      setEnviando(false);
      if (!result.ok) {
        setErro(result.problem.title);
        return;
      }
      setDesafioId(result.value.challenge_id);
      setFinalidadeEmCurso(FINALIDADE_REABERTURA_FINAL);
      setSituacao({ tipo: 'login', etapa: 'segundo-passo' });
    })();
  };

  const reabrirFinal = (reason: string): void => {
    if (
      painelFinal === null ||
      provaReautenticacao?.purpose !== FINALIDADE_REABERTURA_FINAL ||
      provaReautenticacao.participationId !== painelFinal.participation_id ||
      !finalIdentityConfirmed ||
      reason !== finalReopenReason.trim()
    )
      return;
    const status = painelFinal;
    const token = provaReautenticacao.token;
    setEnviando(true);
    setErro(undefined);
    void (async () => {
      const result = await turmas.reopenFinal({
        participationId: status.participation_id,
        revision: status.revision,
        reason,
        reauthToken: token,
        idempotencyKey: crypto.randomUUID(),
      });
      setEnviando(false);
      if (!result.ok) {
        setErro(result.problem.title);
        return;
      }
      setProvaReautenticacao(null);
      setFinalReopenReason('');
      setFinalIdentityConfirmed(false);
      await carregarStatusFinal(status.participation_id);
    })();
  };

  const pedirReautenticacaoCorrecaoInicial = (
    participationId: string,
    purpose: typeof FINALIDADE_REABERTURA_INICIAL | typeof FINALIDADE_COMPARATIVO_EXCEPCIONAL,
  ): void => {
    setEnviando(true);
    setErro(undefined);
    setParticipacaoInicialEmFoco(participationId);
    void (async () => {
      const result = await api.requestStepUp({ purpose });
      setEnviando(false);
      if (!result.ok) {
        setErro(result.problem.title);
        return;
      }
      setDesafioId(result.value.challenge_id);
      setFinalidadeEmCurso(purpose);
      setSituacao({ tipo: 'login', etapa: 'segundo-passo' });
    })();
  };

  const reabrirInicial = (reason: string): void => {
    if (
      painelCorrecaoInicial === null ||
      provaReautenticacao?.purpose !== FINALIDADE_REABERTURA_INICIAL ||
      provaReautenticacao.participationId !== painelCorrecaoInicial.participation_id
    )
      return;
    const status = painelCorrecaoInicial;
    setEnviando(true);
    setErro(undefined);
    void (async () => {
      const result = await turmas.reopenInitial({
        participationId: status.participation_id,
        revision: status.revision,
        reason,
        reauthToken: provaReautenticacao.token,
        idempotencyKey: crypto.randomUUID(),
      });
      setEnviando(false);
      if (!result.ok) {
        setErro(result.problem.title);
        return;
      }
      setProvaReautenticacao(null);
      setComparativoExcepcional(null);
      await carregarStatusCorrecaoInicial(status.participation_id);
    })();
  };

  const aceitarComparativoExcepcional = (reason: string): void => {
    if (
      painelCorrecaoInicial?.correction_case_id === null ||
      painelCorrecaoInicial === null ||
      provaReautenticacao?.purpose !== FINALIDADE_COMPARATIVO_EXCEPCIONAL ||
      provaReautenticacao.participationId !== painelCorrecaoInicial.participation_id
    )
      return;
    const status = painelCorrecaoInicial;
    setEnviando(true);
    setErro(undefined);
    void (async () => {
      const result = await turmas.acceptExceptionalComparison({
        participationId: status.participation_id,
        revision: status.revision,
        correctionCaseId: status.correction_case_id!,
        reason,
        reauthToken: provaReautenticacao.token,
        idempotencyKey: crypto.randomUUID(),
      });
      setEnviando(false);
      if (!result.ok) {
        setErro(result.problem.title);
        return;
      }
      setProvaReautenticacao(null);
      setComparativoExcepcional(result.value);
      await carregarStatusCorrecaoInicial(status.participation_id);
    })();
  };

  const baixarDocumento = (documentId: string): void => {
    if (
      provaReautenticacao === null ||
      provaReautenticacao.purpose !== FINALIDADE_DOWNLOAD_DOCUMENTO
    ) {
      return;
    }
    setDocumentoEmDownload(documentId);
    setErro(undefined);
    void (async () => {
      const result = await documentos.download({
        documentId,
        reauthToken: provaReautenticacao.token,
        idempotencyKey: crypto.randomUUID(),
      });
      setDocumentoEmDownload(null);
      setProvaReautenticacao(null);
      if (!result.ok) {
        setErro(result.problem.title);
        return;
      }
      const url = URL.createObjectURL(result.value.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.value.filename;
      link.click();
      URL.revokeObjectURL(url);
    })();
  };

  const criarTurma = (input: {
    code: string;
    name: string;
    startDate: string;
    endDate: string;
  }): void => {
    setEnviando(true);
    setErro(undefined);

    void (async () => {
      const resultado = await turmas.create({ ...input, idempotencyKey: crypto.randomUUID() });
      setEnviando(false);

      if (!resultado.ok) {
        setErro(resultado.problem.title);
        return;
      }
      await carregarTurmas();
    })();
  };

  const executarAcaoTurma = (action: CohortAction): void => {
    if (
      provaReautenticacao === null ||
      provaReautenticacao.purpose !== FINALIDADE_TURMA_ACAO ||
      turmaEmFocoId === null
    ) {
      return;
    }
    setEnviando(true);
    setErro(undefined);

    void (async () => {
      const resultado = await turmas.executeAction({
        cohortId: turmaEmFocoId,
        action,
        reauthToken: provaReautenticacao.token,
        idempotencyKey: crypto.randomUUID(),
      });
      setEnviando(false);

      if (!resultado.ok) {
        setErro(resultado.problem.title);
        return;
      }
      // A prova é de uso único: consumida, deixa de valer.
      setProvaReautenticacao(null);
      await abrirTurma(turmaEmFocoId);
    })();
  };

  const editarTurma = (input: { name: string; startDate: string; endDate: string }): void => {
    if (situacao.tipo !== 'turma') return;
    const atual = situacao.cohort;
    setEnviando(true);
    setErro(undefined);
    void (async () => {
      const resultado = await turmas.update({
        cohortId: atual.cohort_id,
        revision: atual.revision,
        ...input,
      });
      setEnviando(false);
      if (!resultado.ok) {
        setErro(resultado.problem.title);
        return;
      }
      await abrirTurma(atual.cohort_id);
    })();
  };

  const revogarUm = (deviceId: string): void => {
    if (provaReautenticacao === null || provaReautenticacao.purpose !== FINALIDADE_REVOGACAO) {
      return;
    }
    setEnviando(true);

    void (async () => {
      const resultado = await api.revokeDevice({
        deviceId,
        reauthToken: provaReautenticacao.token,
        idempotencyKey: crypto.randomUUID(),
      });
      setEnviando(false);

      if (!resultado.ok) {
        setErro(resultado.problem.title);
        return;
      }
      // A prova é de uso único: consumida, deixa de valer.
      setProvaReautenticacao(null);
      await carregarDispositivos();
    })();
  };

  const revogarTodos = (): void => {
    if (provaReautenticacao === null || provaReautenticacao.purpose !== FINALIDADE_REVOGACAO) {
      return;
    }
    setEnviando(true);

    void (async () => {
      const resultado = await api.revokeAllDevices({
        reauthToken: provaReautenticacao.token,
        idempotencyKey: crypto.randomUUID(),
      });
      setEnviando(false);

      if (!resultado.ok) {
        setErro(resultado.problem.title);
        return;
      }
      setProvaReautenticacao(null);
      await carregarDispositivos();
    })();
  };

  const autenticada =
    situacao.tipo === 'dispositivos' ||
    situacao.tipo === 'turmas' ||
    situacao.tipo === 'turma' ||
    situacao.tipo === 'metodologias' ||
    situacao.tipo === 'participantes' ||
    situacao.tipo === 'conteudo' ||
    situacao.tipo === 'backups' ||
    situacao.tipo === 'privacidade' ||
    situacao.tipo === 'editorial' ||
    situacao.tipo === 'operacao';

  return (
    <>
      <SkipLink targetId="main-content" />
      <Header title="Meu Mapa NEV" subtitle="Administração">
        {autenticada && (
          <nav className="admin-navigation" aria-label="Áreas administrativas">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSituacao({ tipo: 'metodologias' })}
            >
              Metodologias
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSituacao({ tipo: 'participantes' })}
            >
              Participantes
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSituacao({ tipo: 'conteudo' })}
            >
              Textos e versões
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSituacao({ tipo: 'operacao' })}
            >
              Operação
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSituacao({ tipo: 'backups' })}
            >
              Backups
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSituacao({ tipo: 'privacidade' })}
            >
              Privacidade e exportações
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSituacao({ tipo: 'editorial' })}
            >
              Jurídicos e mensagens
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void carregarTurmas();
              }}
            >
              Turmas
            </Button>
            <Button type="button" variant="outline" onClick={() => void carregarDispositivos()}>
              Dispositivos
            </Button>
          </nav>
        )}
      </Header>

      <main id="main-content" tabIndex={-1} style={{ padding: 'var(--nev-space-6) 0' }}>
        <Container size="content">
          <PanelBoundary key={situacao.tipo}>
            <React.Suspense fallback={<p role="status">Carregando painel…</p>}>
              {situacao.tipo === 'verificando-sessao' && (
                <p role="status" aria-live="polite">
                  Verificando sessão…
                </p>
              )}

              {situacao.tipo === 'contexto-indisponivel' && (
                <section>
                  <AlertComFoco type="critical" title="Não foi possível verificar sua sessão">
                    Verifique a conexão e tente novamente. Nenhuma ação administrativa foi
                    realizada.
                  </AlertComFoco>
                  <Button
                    type="button"
                    onClick={() => {
                      setSituacao({ tipo: 'verificando-sessao' });
                      void verificarSessao();
                    }}
                  >
                    Tentar novamente
                  </Button>
                </section>
              )}

              {erro !== undefined && situacao.tipo !== 'login' && (
                <AlertComFoco type="critical" title="Não foi possível concluir">
                  {erro}
                </AlertComFoco>
              )}

              {situacao.tipo === 'login' && (
                <TelaDeLoginAdmin
                  etapa={situacao.etapa}
                  desafioAntibot={
                    situacao.etapa === 'senha' ? <DesafioAntibot antibot={antibot} /> : undefined
                  }
                  onSubmitPassword={entrarComSenha}
                  onSubmitCode={conferirSegundoPasso}
                  submitting={enviando}
                  erro={erro}
                />
              )}

              {situacao.tipo === 'adesao-dispositivo' && (
                <AdesaoDispositivo
                  onConfirm={() => {
                    void carregarDispositivos();
                  }}
                  onSkip={() => {
                    void carregarDispositivos();
                  }}
                  submitting={enviando}
                />
              )}

              {situacao.tipo === 'dispositivos' && (
                <TelaDeDispositivos
                  devices={situacao.devices}
                  onRequestReauthentication={pedirReautenticacao}
                  onRevokeOne={revogarUm}
                  onRevokeAll={revogarTodos}
                  hasReauthentication={
                    provaReautenticacao !== null &&
                    provaReautenticacao.purpose === FINALIDADE_REVOGACAO
                  }
                  submitting={enviando}
                />
              )}

              {situacao.tipo === 'metodologias' && (
                <TelaDeMetodologias
                  onOpenParticipation={(participationId) =>
                    setSituacao({ tipo: 'participantes', participationId })
                  }
                  onOpenOperations={(personId, participationId) =>
                    setSituacao({ tipo: 'operacao', personId, participationId })
                  }
                />
              )}
              {situacao.tipo === 'participantes' && (
                <ParticipantesPage
                  {...(situacao.participationId
                    ? { initialParticipationId: situacao.participationId }
                    : {})}
                  onOpenCohort={(id) => void abrirTurma(id)}
                  onOpenOperations={(personId, participationId) =>
                    setSituacao({ tipo: 'operacao', personId, participationId })
                  }
                />
              )}
              {situacao.tipo === 'conteudo' && <TelaDeConteudo />}
              {situacao.tipo === 'backups' && <TelaDeBackups />}
              {situacao.tipo === 'privacidade' && <TelaDePrivacidade />}
              {situacao.tipo === 'editorial' && <TelaEditorial />}
              {situacao.tipo === 'operacao' && (
                <TelaDeOperacao
                  {...(situacao.personId ? { initialPersonId: situacao.personId } : {})}
                  {...(situacao.participationId
                    ? { initialParticipationId: situacao.participationId }
                    : {})}
                />
              )}
              {situacao.tipo === 'turma' && (
                <details>
                  <summary>Checklist, versões e controles de acesso</summary>
                  <TelaDeLiberacao cohortId={situacao.cohort.cohort_id} />
                </details>
              )}
              {situacao.tipo === 'turmas' && (
                <TelaDeTurmas
                  cohorts={situacao.cohorts}
                  onCreate={criarTurma}
                  onOpen={(cohortId) => {
                    void abrirTurma(cohortId);
                  }}
                  submitting={enviando}
                />
              )}

              {situacao.tipo === 'turma' && (
                <TelaDeTurma
                  cohort={situacao.cohort}
                  participants={situacao.participants}
                  onAction={executarAcaoTurma}
                  onUpdate={editarTurma}
                  onRequestReauthentication={pedirReautenticacaoTurma}
                  hasReauthentication={
                    provaReautenticacao !== null &&
                    provaReautenticacao.purpose === FINALIDADE_TURMA_ACAO
                  }
                  onBack={() => {
                    setPainelDocumentos(null);
                    setPainelFinal(null);
                    setPainelCorrecaoInicial(null);
                    setComparativoExcepcional(null);
                    void carregarTurmas();
                  }}
                  documentsPanel={painelDocumentos}
                  onOpenDocuments={(participationId) => {
                    void carregarDocumentos(participationId);
                  }}
                  onCloseDocuments={() => setPainelDocumentos(null)}
                  onRequestDocumentReauthentication={pedirReautenticacaoDocumento}
                  onDownloadDocument={baixarDocumento}
                  hasDocumentReauthentication={
                    provaReautenticacao !== null &&
                    provaReautenticacao.purpose === FINALIDADE_DOWNLOAD_DOCUMENTO
                  }
                  downloadingDocumentId={documentoEmDownload}
                  finalStatusPanel={painelFinal}
                  onOpenFinalStatus={(participationId) => {
                    if (participationId !== participacaoFinalEmFoco) {
                      setFinalReopenReason('');
                      setFinalIdentityConfirmed(false);
                    }
                    void carregarStatusFinal(participationId);
                  }}
                  onCloseFinalStatus={() => setPainelFinal(null)}
                  onRequestFinalReauthentication={pedirReautenticacaoFinal}
                  hasFinalReauthentication={
                    provaReautenticacao?.purpose === FINALIDADE_REABERTURA_FINAL &&
                    provaReautenticacao.participationId === painelFinal?.participation_id
                  }
                  onReopenFinal={reabrirFinal}
                  finalReopenReason={finalReopenReason}
                  onFinalReopenReasonChange={setFinalReopenReason}
                  finalIdentityConfirmed={finalIdentityConfirmed}
                  onFinalIdentityConfirmedChange={setFinalIdentityConfirmed}
                  initialCorrectionStatusPanel={painelCorrecaoInicial}
                  onOpenInitialCorrectionStatus={(participationId) => {
                    setComparativoExcepcional(null);
                    void carregarStatusCorrecaoInicial(participationId);
                  }}
                  onCloseInitialCorrectionStatus={() => {
                    setPainelCorrecaoInicial(null);
                    setComparativoExcepcional(null);
                  }}
                  onRequestInitialReauthentication={(participationId) =>
                    pedirReautenticacaoCorrecaoInicial(
                      participationId,
                      FINALIDADE_REABERTURA_INICIAL,
                    )
                  }
                  hasInitialReauthentication={
                    provaReautenticacao?.purpose === FINALIDADE_REABERTURA_INICIAL &&
                    provaReautenticacao.participationId === painelCorrecaoInicial?.participation_id
                  }
                  onReopenInitial={reabrirInicial}
                  onRequestExceptionalReauthentication={(participationId) =>
                    pedirReautenticacaoCorrecaoInicial(
                      participationId,
                      FINALIDADE_COMPARATIVO_EXCEPCIONAL,
                    )
                  }
                  hasExceptionalReauthentication={
                    provaReautenticacao?.purpose === FINALIDADE_COMPARATIVO_EXCEPCIONAL &&
                    provaReautenticacao.participationId === painelCorrecaoInicial?.participation_id
                  }
                  onAcceptExceptionalComparison={aceitarComparativoExcepcional}
                  exceptionalComparison={comparativoExcepcional}
                  submitting={enviando}
                />
              )}
            </React.Suspense>
          </PanelBoundary>
        </Container>
      </main>
    </>
  );
};

export default App;
